import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Stack, IconButton, InputAdornment, Collapse, Button } from '@mui/material';
import RoomIcon from '@mui/icons-material/Room';
// ;
import { decodeAddressGeoposition } from '../services/storeService';
import MapDisplay  from "@components/MapDisplay.tsx";

interface LocationInputProps {
  address: string;
  lat: number;
  lng: number;
  onChange: (value: { address: string; lat: number; lng: number }) => void;
  compact?: boolean;
  disabled?: boolean;
  markerLabel?: string;
}

const LocationInput: React.FC<LocationInputProps> = ({ address, lat, lng, onChange, compact, disabled, markerLabel }) => {
  const [localAddress, setLocalAddress] = useState(address);
  const [localLat, setLocalLat] = useState(lat);
  const [localLng, setLocalLng] = useState(lng);
  const [loading, setLoading] = useState(false);
  const [showGeo, setShowGeo] = useState(false);
  const [addressFocused, setAddressFocused] = useState(false);
  const blurTimeout = useRef<NodeJS.Timeout | null>(null);
  const [_enterPressed, setEnterPressed] = useState(false);

  useEffect(() => {
    setLocalAddress(address);
    setLocalLat(lat);
    setLocalLng(lng);
  }, [address, lat, lng]);

  const handleAddressBlur = async (fromEnter = false) => {
    if (!fromEnter) {
      setAddressFocused(false);
      blurTimeout.current = setTimeout(() => setShowGeo(false), 150);
    }
    if (!localAddress) return;
    setLoading(true);
    try {
      const geo = await decodeAddressGeoposition(localAddress);
      setLocalLat(geo.lat);
      setLocalLng(geo.lng);
      onChange({ address: localAddress, lat: geo.lat, lng: geo.lng });
    } catch (err) {
      // Optionally handle error
    } finally {
      setLoading(false);
    }
  };

  const handleAddressFocus = () => {
    setAddressFocused(true);
    setShowGeo(true);
    if (blurTimeout.current) clearTimeout(blurTimeout.current);
  };

  const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setLocalLat(value);
    onChange({ address: localAddress, lat: value, lng: localLng });
  };

  const handleLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setLocalLng(value);
    onChange({ address: localAddress, lat: localLat, lng: value });
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalAddress(e.target.value);
    onChange({ address: e.target.value, lat: localLat, lng: localLng });
  };

  const handleShowGeoClick = () => {
    setShowGeo((prev) => !prev);
  };

  // Show geo/map if toggled or address is focused
  const geoVisible = showGeo || addressFocused;

  return (
    <Box sx={{ width: compact ? 320 : '100%', p: compact ? 1 : 2, border: '1px solid #eee', borderRadius: 2, background: '#fafbfc' }}>
      <Stack spacing={1} direction={compact ? 'column' : 'row'} alignItems={compact ? 'stretch' : 'center'}>
        <TextField
          label="Address"
          value={localAddress}
          onChange={handleAddressChange}
          onBlur={() => handleAddressBlur(false)}
          onFocus={handleAddressFocus}
          size={compact ? 'small' : 'medium'}
          fullWidth
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              setEnterPressed(true);
              handleAddressBlur(true);
            }
          }}
          InputProps={{
            endAdornment: !disabled && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleAddressBlur()} disabled={loading}>
                  <RoomIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {!disabled && (
          <Button variant="outlined" size="small" onClick={handleShowGeoClick} sx={{ minWidth: 90 }}>
            {geoVisible ? 'Hide Map' : 'Show Map'}
          </Button>
        )}
      </Stack>
      <Collapse in={geoVisible || disabled}>
        <Stack direction="row" spacing={1} mt={1} alignItems="center">
          <TextField
            label="Lat"
            value={localLat}
            onChange={handleLatChange}
            size={compact ? 'small' : 'medium'}
            style={{ width: 100 }}
            disabled={disabled}
          />
          <TextField
            label="Lng"
            value={localLng}
            onChange={handleLngChange}
            size={compact ? 'small' : 'medium'}
            style={{ width: 100 }}
            disabled={disabled}
          />
        </Stack>
        <Box mt={1}>
          <MapDisplay lat={localLat} lng={localLng} label={markerLabel} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default LocationInput; 